import type { DistributorBranding } from "creso-core/types";
import { isProduction } from "@/utils/common";

const stockLoginBannersOrigin = () =>
  isProduction()
    ? "https://prod-creso-news-media.s3.amazonaws.com"
    : "https://nonprod-creso-news-media.s3.amazonaws.com";
const STOCK_LOGIN_BANNERS_PATH =
  "/media/brands/backgrounds/brand_backgrounds/default";

export const STOCK_LOGIN_BANNERS = [
  "login-bg-1",
  "login-bg-2",
  "login-bg-3",
] as const;

export type StockLoginBanner = (typeof STOCK_LOGIN_BANNERS)[number];

// Same image as the fallback bundled with the client web app
// (assets/login-banner.webp), shown when a distributor hasn't saved a banner
export const DEFAULT_LOGIN_BANNER: StockLoginBanner = "login-bg-2";

const stockLoginBannerPath = (banner: StockLoginBanner) =>
  `${STOCK_LOGIN_BANNERS_PATH}/${banner}.webp`;

export const stockLoginBannerUrl = (banner: StockLoginBanner) =>
  `${stockLoginBannersOrigin()}${stockLoginBannerPath(banner)}`;

/** The banner the distributor saved, if they have one */
export function getSavedLoginBannerUrl(branding?: DistributorBranding) {
  return typeof branding?.background_image === "string" &&
    branding.background_image
    ? branding.background_image
    : null;
}

/** The banner the client login page shows */
export function getLoginBannerUrl(branding?: DistributorBranding) {
  return (
    getSavedLoginBannerUrl(branding) ??
    stockLoginBannerUrl(DEFAULT_LOGIN_BANNER)
  );
}

/** The stock banner a saved banner URL points to, if it is one */
export function getStockLoginBanner(url: string): StockLoginBanner | null {
  let pathname;
  try {
    pathname = new URL(url, window.location.origin).pathname;
  } catch {
    return null;
  }
  // Matched on the path alone, since the API may serve the banner from another host
  return (
    STOCK_LOGIN_BANNERS.find((banner) =>
      pathname.endsWith(stockLoginBannerPath(banner))
    ) ?? null
  );
}
